import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Instance, cast } from 'mobx-state-tree'
import { IconButton, Paper, Divider, TextField, InputAdornment } from '@mui/material'
import { Add, Search } from '@mui/icons-material'
import { Close } from '@mui/icons-material'

import { Button } from '@trivie/ui-web/components/button'
import { color } from '@trivie/ui-web/theme'
import { Text } from '@trivie/ui-web/components/text'
import { AssignmentUsersDialog } from './assign-users'
import { SurveyUser, SurveyGroup } from '@trivie/core/src/models/survey/SurveyUserGroup'
import { useStores } from '@trivie/core'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

import { GroupItem } from './group-item'
import { UserItem } from './user-item'

export const PeopleCard: React.FunctionComponent = observer(() => {
  const [openAssignDialog, setOpenAssignDialog] = useState(false)
  const [filter, setFilter] = useState('')

  const { surveyStore } = useStores()
  const { selectedSurvey, updateSurveyAssignees, getSurveyDetails } = surveyStore
  const isCompleted = selectedSurvey?.isCompleted

  const confirm = useConfirm()

  const handleRemove = (user_id: number | null, group_id: number | null) => {
    const user = selectedSurvey?.users.find((u) => u.user_id === user_id)
    const group = selectedSurvey?.groups.find((g) => g.group_id === group_id)

    confirm({
      danger: true,
      title: `Remove ${user_id ? 'User' : 'Group'}`,
      confirmationText: 'Remove',
      hideCancelButton: true,
      dialogText: user
        ? `Are you sure you want to remove ${user.display_name} from the survey?`
        : `Are you sure you want to remove the "${group?.label}" group from the survey?`,
    })
      .then(async () => {
        if (selectedSurvey && selectedSurvey.id) {
          const data = {
            group_ids: selectedSurvey?.groups
              .filter((gr: Instance<typeof SurveyGroup>) => gr.group_id !== group_id)
              .map((gr: Instance<typeof SurveyGroup>) => gr.group_id),
            user_ids: selectedSurvey?.users
              .filter((usr: Instance<typeof SurveyUser>) => usr.user_id !== user_id)
              .map((usr: Instance<typeof SurveyUser>) => usr.user_id),
          }
          const success = await updateSurveyAssignees(selectedSurvey.id, cast(data))
          if (success) {
            getSurveyDetails(selectedSurvey.id)
            confirm({
              title: 'Success',
              dialogText: user
                ? `${user.display_name} has been removed from the survey`
                : `The "${group?.label}" group has been removed from the survey`,
              hideCancelButton: true,
              confirmationText: 'Ok',
            })
              .then(() => {})
              .catch(() => {})
          } else {
            confirm({
              danger: true,
              title: 'Error',
              dialogText: user
                ? `There was an issue removing ${user.display_name} from the survey`
                : `There was an issue removing the ${group?.label} group from the survey`,
              hideCancelButton: true,
              confirmationText: 'Ok',
            })
          }
        }
      })
      .catch(() => {})
  }

  const getFilteredUsers = () =>
    selectedSurvey?.users?.filter((usr) =>
      filter
        ? usr.display_name?.toLowerCase()?.includes(filter?.toLowerCase()) ||
          usr.email?.toLowerCase()?.includes(filter.toLowerCase())
        : true,
    )

  const getFilteredGroups = () =>
    selectedSurvey?.groups?.filter((grp) =>
      filter ? grp?.label?.toLowerCase()?.includes(filter?.toLowerCase()) : true,
    )

  return (
    <Paper variant="outlined" style={CONTAINER}>
      <div style={HEADER}>
        <Text variant="body1Medium" text={'People'} />
        {isCompleted ? null : (
          <Button
            onClick={() => setOpenAssignDialog(true)}
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Add />}
            text="Add Users"
          />
        )}
      </div>
      <Divider
        style={{ marginTop: 20, marginBottom: 20, backgroundColor: color.trivieGrey, opacity: 0.2 }}
      />

      <div style={{ paddingLeft: 20, paddingRight: 20, marginBottom: 24 }}>
        {isCompleted ? null : (
          <TextField
            fullWidth
            variant="standard"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search htmlColor={color.palette.grey} />
                  </InputAdornment>
                ),
              },
            }}
            id="standard-search"
            placeholder="Search"
            type="search"
            onChange={(e) => setFilter(e.target.value)}
          />
        )}
      </div>
      <div
        style={{
          maxHeight: 400,
          overflow: 'auto',
          paddingRight: 20,
          paddingLeft: 20,
        }}
      >
        {selectedSurvey?.groups?.length ? (
          <Text
            variant="body2Emphasized"
            text={'GROUPS'}
            component="div"
            style={{ marginBottom: 16, color: color.shade50 }}
          />
        ) : null}
        {getFilteredGroups()?.map((group, idx) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: idx === getFilteredGroups()!.length - 1 ? 20 : 12,
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1 }}>
              <GroupItem group={group} />
            </div>
            {isCompleted ? null : (
              <IconButton
                size="large"
                sx={{ width: 40, height: 40 }}
                onClick={() => handleRemove(null, group.group_id)}
              >
                <Close htmlColor={color.shade50} style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>
        ))}
        {selectedSurvey?.users?.length ? (
          <Text
            variant="body2Emphasized"
            text={'USERS'}
            component="div"
            style={{ color: color.shade50, marginBottom: 16 }}
          />
        ) : null}
        {getFilteredUsers()?.map((user) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: 16,
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1 }}>
              <UserItem user={user} />
            </div>
            {isCompleted ? null : (
              <IconButton
                size="large"
                sx={{ width: 40, height: 40 }}
                onClick={() => handleRemove(user.user_id, null)}
              >
                <Close htmlColor={color.shade50} style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>
        ))}
      </div>
      <AssignmentUsersDialog
        open={openAssignDialog}
        requestClose={() => setOpenAssignDialog(false)}
      />
    </Paper>
  )
})

const CONTAINER: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 20,
  paddingTop: 20,
  paddingBottom: 20,
}

const HEADER: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingLeft: 20,
  paddingRight: 20,
}
